import React from 'react'

import AvatarArea from './AvatarArea'
import Content from './Content'

import { useNewConversationCount } from '@SonarCX/providers/NewConversationCountProvider'
import { useQueueState } from '@SonarCX/providers/QueueStateProvider'
import useStyles from './styles'

const BaseConversation = ({
  contentProps,
  avatarProps,
  ContentComponent = Content,
  onClick,
  isGroup,
  sorting,
  scrollID,
  isPersonalSpace = false
}) => {
  // Gets props from Conversations

  const { updatePosition } = useNewConversationCount()
  const { scrollTo, setScrollTo } = useQueueState()

  const [hovering, setHovering] = React.useState(false)
  const ref = React.useRef(null)

  const classes = useStyles({
    selected: contentProps.selected,
    isNew: contentProps.isNew,
    isGroup,
    isPersonalSpace
  })

  // For handling new message indicator
  React.useEffect(() => {
    let mounted = true

    const onWheel = () => {
      if (ref.current && mounted) {
        updatePosition({ id: scrollID, isNew: contentProps.isNew, ref })
      }
    }

    window.addEventListener('wheel', onWheel)

    return () => {
      mounted = false
      window.removeEventListener('wheel', onWheel)
    }
  }, [contentProps.isNew, scrollID, updatePosition])

  // Sets update flag when new message comes in or sorting changes
  React.useEffect(() => {
    updatePosition({ id: scrollID, isNew: contentProps.isNew, ref })
  }, [contentProps.isNew, scrollID, updatePosition, sorting])

  const [intersect, setIntersect] = React.useState(null)
  const [element, setElement] = React.useState(null)
  // observer variable to set viewport and check if user is in it
  const observer = React.useRef(
    new IntersectionObserver(
      entries => {
        setIntersect(entries[0])
      },
      { root: document.querySelector('#conversations'), threshold: 0 }
    )
  )

  // effect for watching and unwatching observer variable
  React.useEffect(() => {
    const currentElement = element
    const currentObserver = observer?.current

    if (currentElement) {
      currentObserver.observe(currentElement)
    }

    // clean up observer to stop watching
    return () => {
      if (currentElement) {
        currentObserver.unobserve(currentElement)
      }
    }
  }, [element])

  // Scrolls to newly added convo
  React.useEffect(() => {
    if (ref.current && scrollTo && scrollTo === scrollID) {
      const performScroll = () => {
        if (ref.current?.scrollIntoView) {
          ref.current.scrollIntoView(true)
        }
      }
      performScroll()
      setTimeout(() => {
        performScroll()
        setScrollTo(null)
      }, 500)
    }
  }, [scrollTo, scrollID, setScrollTo])

  const handleMouseEnter = React.useCallback(() => {
    setHovering(true)
  }, [])

  const handleMouseLeave = React.useCallback(() => {
    setHovering(false)
  }, [])

  return (
    <div ref={ref} className={classes.holder}>
      <div
        className={classes.root}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onClick}
        ref={setElement}
      >
        <div className={classes.type} />

        <AvatarArea {...avatarProps} intersect={intersect} />

        <ContentComponent {...contentProps} hovering={hovering} />
      </div>

      {contentProps.selected && <div className={classes.border} />}
    </div>
  )
}

export default BaseConversation
